/**
 * Firestore bindings + types pour `/users/{uid}/billing/current`.
 *
 * Un document unique (doc id = "current") décrit l'état d'abonnement et de
 * crédits d'un utilisateur. On le lit en live (onSnapshot) depuis le hook
 * `useEntitlements`, qui expose aux composants UI :
 *   - le plan actif (ou "free"),
 *   - le nombre de crédits restants (fiches "incluses" + top-ups),
 *   - la date d'expiration du crédit gratuit d'inscription (1 fiche, 7 jours),
 *   - le droit de créer une fiche (`canCreateFiche`).
 *
 * Scaffold Stripe : `stripeCustomerId` / `stripeSubscriptionId` sont câblés
 * côté types + lecture seule client. L'écriture passe par les Cloud Functions
 * (`createCheckoutSession`, webhook `stripeWebhook`) qui ont l'Admin SDK.
 *
 * Règles Firestore : le client peut lire son propre doc mais **pas écrire**
 * directement — toute mutation passe par une callable côté serveur (bootstrap
 * du free credit à l'inscription, decrement après création de fiche, update
 * du plan après Stripe webhook). Garantit que le client ne peut pas s'auto-
 * créditer.
 */

import {
  doc,
  onSnapshot,
  Timestamp,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "./firebase";
import type { PlanId } from "../data/pricing";

/** État d'un abonnement Stripe — miroir des statuts upstream qu'on consomme. */
export type SubscriptionStatus =
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "none"; // aucun abonnement — utilisateur free

/** Un lot de crédits top-up acheté à un moment donné. Les crédits d'un même
 *  achat expirent ensemble (12 mois glissant), ce qui évite d'avoir à tracker
 *  l'expiration par unité.
 */
export type TopUpBatch = {
  purchasedAt: Timestamp;
  /** Nombre initial de crédits achetés. */
  amount: number;
  /** Nombre restant (décrémenté à chaque fiche créée). */
  remaining: number;
  /** Date d'expiration (purchasedAt + validityMonths, typiquement +12 mois). */
  expiresAt: Timestamp;
  /** ID du charge/session Stripe — audit trail. */
  stripeChargeId: string | null;
};

/** Document Firestore `/users/{uid}/billing/current`. */
export type BillingDoc = {
  /** Plan actif. "free" = aucun abonnement, utilise le crédit gratuit. */
  plan: PlanId | "free";
  /** Statut d'abonnement Stripe. "none" quand plan === "free". */
  status: SubscriptionStatus;

  /** Crédits "inclus" restants dans le cycle de facturation courant. Reset au
   * 1er de chaque mois par un job planifié (à implémenter côté Cloud Functions). */
  creditsRemaining: number;
  /** Timestamp du dernier reset mensuel — sert au job de recharge à savoir
   *  quand ne pas re-créditer. */
  creditsAllocatedAt: Timestamp | null;

  /** Crédit gratuit d'inscription : 1 fiche, valable 7 jours. null quand
   *  consommé ou expiré. */
  freeCredit: {
    remaining: number;
    expiresAt: Timestamp;
  } | null;

  /** Lots de crédits top-up (valides 12 mois). Ordre FIFO côté consommation
   *  pour consommer d'abord ce qui expire le plus tôt. */
  topUps: TopUpBatch[];

  /** Stripe — scaffold, nullable tant que l'utilisateur n'est pas passé en checkout. */
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  /** Cycle de facturation actif — informatif côté UI. */
  billingCycle: "engagement" | "monthly" | null;

  /** Date de fin programmée quand l'utilisateur a cliqué "Annuler" dans le
   *  Customer Portal. Stripe applique `cancel_at_period_end: true` et la sub
   *  reste `active` jusqu'à `cancelAt`. null = pas de résiliation programmée.
   *  Source : `subscription.cancel_at` dans le webhook `customer.subscription.updated`. */
  cancelAt: Timestamp | null;

  /** Fin de la période de facturation courante = prochaine date de
   *  renouvellement (ou de fin si `cancelAt` est défini). Affiché sur
   *  Settings → Abonnement pour répondre à la question "quand est ma
   *  prochaine facture ?". Source : `sub.items.data[0].current_period_end`
   *  (en sec depuis l'API 2026-04-22.dahlia, l'ancien top-level a été
   *  retiré). null tant qu'il n'y a pas de sub active. */
  currentPeriodEnd: Timestamp | null;

  /** Métadonnées. */
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
};

// ─── Paths ───────────────────────────────────────────────────────────────
function billingRef(uid: string) {
  return doc(db, "users", uid, "billing", "current");
}

// ─── Lecture live ────────────────────────────────────────────────────────
export function subscribeBilling(
  uid: string,
  cb: (billing: BillingDoc | null) => void,
): Unsubscribe {
  return onSnapshot(billingRef(uid), (snap) => {
    cb(snap.exists() ? (snap.data() as BillingDoc) : null);
  });
}

// ─── Dérivations — logique de gating ─────────────────────────────────────

/** Total des crédits disponibles pour créer une fiche : inclus dans le plan
 *  + top-ups non-expirés + crédit gratuit non-expiré. */
export function totalAvailableCredits(billing: BillingDoc | null): number {
  if (!billing) return 0;
  const now = Date.now();

  const free =
    billing.freeCredit && billing.freeCredit.expiresAt.toMillis() > now
      ? billing.freeCredit.remaining
      : 0;

  const topUps = billing.topUps.reduce(
    (sum, batch) =>
      batch.expiresAt.toMillis() > now ? sum + batch.remaining : sum,
    0,
  );

  return billing.creditsRemaining + topUps + free;
}

/** L'utilisateur peut-il créer une nouvelle fiche ? Gating unique consommé
 *  par `Nouvelle.tsx` et la nav shell. */
export function canCreateFiche(billing: BillingDoc | null): boolean {
  if (!billing) return false;
  // On autorise uniquement les plans actifs ou le free credit non-expiré.
  // `past_due` et `canceled` bloquent même s'il reste des crédits (alignement
  // incident → relance le paiement). `trialing` = OK.
  if (
    billing.plan !== "free" &&
    billing.status !== "active" &&
    billing.status !== "trialing"
  ) {
    return false;
  }
  return totalAvailableCredits(billing) > 0;
}

/** Raison humaine pour laquelle la création est bloquée — consommé par la
 *  modale "Crédits épuisés". */
export type GatingReason =
  | "no-subscription-no-credits"
  | "free-credit-expired"
  | "monthly-quota-exhausted"
  | "subscription-past-due"
  | "subscription-canceled"
  | null;

export function gatingReason(billing: BillingDoc | null): GatingReason {
  if (!billing) return "no-subscription-no-credits";

  if (billing.status === "past_due") return "subscription-past-due";
  if (billing.status === "canceled") return "subscription-canceled";

  if (canCreateFiche(billing)) return null;

  if (billing.plan === "free") {
    const freeExpired =
      !billing.freeCredit || billing.freeCredit.expiresAt.toMillis() <= Date.now();
    return freeExpired ? "free-credit-expired" : "no-subscription-no-credits";
  }
  return "monthly-quota-exhausted";
}

/** Jours restants avant expiration du crédit gratuit (arrondi vers le bas).
 *  null si pas de crédit gratuit actif. */
export function freeCreditDaysLeft(billing: BillingDoc | null): number | null {
  if (!billing?.freeCredit) return null;
  const ms = billing.freeCredit.expiresAt.toMillis() - Date.now();
  if (ms <= 0) return null;
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

/** Type d'un batch de crédits — utilisé pour la copy contextuelle. */
export type CreditBatchType = "free" | "topup" | "plan";

/** Un batch de crédits avec son origine, son count restant, son expiry. */
export type CreditBatch = {
  type: CreditBatchType;
  /** Count initial à l'achat / à la création. Permet d'afficher "3/5 restantes". */
  amount: number;
  /** Count restant utilisable. Toujours > 0 (les buckets vides sont filtrés). */
  remaining: number;
  /** ms epoch ou null pour "plan" (le quota plan n'expire pas individuellement). */
  expiresAtMs: number | null;
  /** Pour topup : ms epoch d'achat (sert au tri DESC dans l'UI Settings). */
  purchasedAtMs: number | null;
  /** Pour topup : ID Stripe charge/payment_intent — utile pour les liens facture. */
  stripeChargeId: string | null;
};

/** Synthèse pré-calculée des crédits d'un user.
 *
 * Conventions de cette refacto (issue du rapport Cowork batch 2 §6) :
 *   - `totalRemaining` : somme de TOUS les buckets actifs (free + topup + plan).
 *     Source unique de vérité pour le widget sidebar et le compteur global.
 *   - `soonestExpiryMs` : expiry **du même périmètre que `totalRemaining`** —
 *     c'est-à-dire le bucket actif (non-plan) qui expire le plus tôt. Évite
 *     l'inconsistance précédente "3 fiches restantes · 5 jours" où le count
 *     filtrait freeCredit mais l'expiry le prenait quand même.
 *   - `primaryBatch` : le bucket "principal" à mettre en avant dans une copy
 *     courte. Heuristique : (1) plan si l'user en a un et qu'il a des crédits ;
 *     (2) sinon le topup avec le plus de remaining ; (3) sinon free.
 *   - `allBatches` : la liste détaillée triée par expiry (soonest first), pour
 *     le tooltip / la section Settings → Crédits add-on.
 */
export type CreditsSummary = {
  /** Total disponible — matche `totalAvailableCredits()`. */
  totalRemaining: number;
  /** Détail par origine. */
  free: number;
  topup: number;
  plan: number;
  /** Bucket à mettre en avant dans les copies courtes. null si aucun crédit. */
  primaryBatch: CreditBatch | null;
  /** Earliest expiry parmi les buckets non-plan actifs. null = pas de bucket
   *  expirable (uniquement plan, ou rien). */
  soonestExpiryMs: number | null;
  /** Tous les buckets actifs (remaining > 0, non expirés), triés par expiry
   *  asc (les non-expirables — i.e. plan — en queue de liste). */
  allBatches: CreditBatch[];
};

export function summarizeCredits(
  billing: BillingDoc | null,
): CreditsSummary {
  const empty: CreditsSummary = {
    totalRemaining: 0,
    free: 0,
    topup: 0,
    plan: 0,
    primaryBatch: null,
    soonestExpiryMs: null,
    allBatches: [],
  };
  if (!billing) return empty;
  const now = Date.now();

  const batches: CreditBatch[] = [];

  // 1. Free credit (signup, 1 fiche / 7 j).
  let free = 0;
  if (billing.freeCredit && billing.freeCredit.expiresAt.toMillis() > now) {
    free = billing.freeCredit.remaining;
    if (free > 0) {
      batches.push({
        type: "free",
        amount: 1, // signup credit = toujours 1 fiche
        remaining: free,
        expiresAtMs: billing.freeCredit.expiresAt.toMillis(),
        purchasedAtMs: null,
        stripeChargeId: null,
      });
    }
  }

  // 2. Topup batches.
  let topup = 0;
  for (const batch of billing.topUps) {
    if (batch.remaining <= 0) continue;
    const expMs = batch.expiresAt.toMillis();
    if (expMs <= now) continue;
    topup += batch.remaining;
    batches.push({
      type: "topup",
      amount: batch.amount,
      remaining: batch.remaining,
      expiresAtMs: expMs,
      purchasedAtMs: batch.purchasedAt?.toMillis() ?? null,
      stripeChargeId: batch.stripeChargeId,
    });
  }

  // 3. Plan quota (n'expire pas individuellement — reset à chaque renouvellement).
  const plan = billing.creditsRemaining;
  if (plan > 0) {
    batches.push({
      type: "plan",
      amount: plan, // pas d'historique "initial" ; on aligne sur remaining
      remaining: plan,
      expiresAtMs: null,
      purchasedAtMs: null,
      stripeChargeId: null,
    });
  }

  // Tri : par expiry asc, les sans-expiry (plan) en queue.
  batches.sort((a, b) => {
    if (a.expiresAtMs == null && b.expiresAtMs == null) return 0;
    if (a.expiresAtMs == null) return 1;
    if (b.expiresAtMs == null) return -1;
    return a.expiresAtMs - b.expiresAtMs;
  });

  // soonestExpiry = first bucket avec expiry (puisque trié).
  const soonestExpiryMs =
    batches.find((b) => b.expiresAtMs != null)?.expiresAtMs ?? null;

  // primaryBatch heuristique :
  //   - Si l'user a du plan, c'est son repère principal.
  //   - Sinon, le topup avec le plus gros `remaining` (au-dessus du free).
  //   - Sinon, le free.
  let primaryBatch: CreditBatch | null = null;
  const planBatch = batches.find((b) => b.type === "plan");
  const topupBatches = batches.filter((b) => b.type === "topup");
  const freeBatch = batches.find((b) => b.type === "free");
  if (planBatch) {
    primaryBatch = planBatch;
  } else if (topupBatches.length > 0) {
    primaryBatch = topupBatches.reduce((best, b) =>
      b.remaining > best.remaining ? b : best,
    );
  } else if (freeBatch) {
    primaryBatch = freeBatch;
  }

  return {
    totalRemaining: free + topup + plan,
    free,
    topup,
    plan,
    primaryBatch,
    soonestExpiryMs,
    allBatches: batches,
  };
}
